/**
 * §P.2 · Alta y edición de un grupo.
 *
 * Dos cosas que este formulario dice y que no son obvias:
 *
 * **El rango de edad se enseña, no se impone.** El servidor no lo valida a
 * propósito: «un club mete legítimamente a un niño de 11 en el grupo de 12-16
 * porque es grande, o a un hermano pequeño con el mayor para que la familia haga
 * un solo viaje. Una regla dura ahí obligaría a inventar una excepción, y las
 * excepciones son la puerta de atrás que nadie documenta.» Así que aquí es
 * informativo y se dice que lo es.
 *
 * **El aforo sí se respeta**, y por eso se pide con cuidado: es la gente que cabe
 * en el tatami, y un grupo completo responde `409` al matricular.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  camposQueCambiaron,
  esConflictoDeEdicion,
  etagDe,
} from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import {
  disciplinasParaUnGrupo,
  type Disciplina,
  type Grupo,
  type Sede,
} from '@/club/grupos/tipos';
import type { DisciplinaDelClub } from '@/club/ajustes/tipos';

interface Campos {
  name: string;
  venue_id: string;
  discipline_id: string;
  level: string;
  age_min: string;
  age_max: string;
  capacity: string;
}

const ETIQUETAS: Record<string, string> = {
  name: 'el nombre',
  venue_id: 'la sede',
  level: 'el nivel',
  age_min: 'la edad mínima',
  age_max: 'la edad máxima',
  capacity: 'el aforo',
  active: 'si está abierto',
};

const desde = (g: Grupo | null): Campos => ({
  name: g?.name ?? '',
  venue_id: g?.venue_id ?? '',
  discipline_id: g?.discipline_id ?? '',
  level: g?.level ?? '',
  age_min: g?.age_min?.toString() ?? '',
  age_max: g?.age_max?.toString() ?? '',
  capacity: g?.capacity?.toString() ?? '',
});

/** Vacío es `null`; un número mal escrito también, que el servidor lo rechace. */
const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());
const oNumero = (v: string): number | null => {
  const n = Number.parseInt(v.trim(), 10);
  return Number.isFinite(n) ? n : null;
};

export function FormularioDeGrupo({
  club,
  grupo,
  etag,
  suspendido,
  alGuardar,
  alCancelar,
}: {
  club: string;
  grupo: Grupo | null;
  etag: string | null;
  suspendido: boolean;
  alGuardar: (grupoId: string) => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const editando = grupo !== null;

  const [campos, setCampos] = useState<Campos>(() => desde(grupo));
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conflicto, setConflicto] = useState<readonly string[] | null>(null);
  const [etagRecibido, setEtagRecibido] = useState(etag);
  const [etagVivo, setEtagVivo] = useState(etag);

  if (etag !== etagRecibido) {
    setEtagRecibido(etag);
    setEtagVivo(etag);
  }

  const sedes = useQuery({
    queryKey: ['sedes', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).venues.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Sede[];
    },
  });

  /*
   * Con una sola sede no hay nada que elegir: se pone sola. Es el caso de casi todos
   * los clubes, y de quien acaba de crear su primera sede desde aquí (03-11-2026).
   * Se deriva y no se copia al estado: la sede llega después del primer pintado.
   */
  const unicaSede = sedes.data?.length === 1 ? sedes.data[0]!.id : '';
  const sede = campos.venue_id || (editando ? '' : unicaSede);

  // El catálogo entero de Kiap; las del club van primero (ver `disciplinasParaUnGrupo`).
  const disciplinas = useQuery({
    queryKey: ['disciplinas'],
    enabled: !editando,
    queryFn: async () => {
      const { data, error } = await api.v1.disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Disciplina[];
    },
  });

  const delClub = useQuery({
    queryKey: ['disciplinas-del-club', club],
    enabled: !editando,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).disciplines.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DisciplinaDelClub[];
    },
  });

  const { suyas, otras, unica } = disciplinasParaUnGrupo(
    disciplinas.data ?? [],
    delClub.data ?? []
  );
  const disciplina = campos.discipline_id || (editando ? '' : unica);

  const cambiar = (campo: keyof Campos, valor: string) => {
    setCampos((c) => ({ ...c, [campo]: valor }));
    setTocados((t) => new Set(t).add(campo));
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const comun = {
        name: campos.name.trim(),
        level: oNulo(campos.level),
        age_min: oNumero(campos.age_min),
        age_max: oNumero(campos.age_max),
        capacity: oNumero(campos.capacity),
      };

      if (!editando) {
        const r = await api.v1.clubs({ club }).groups.post({
          ...comun,
          venue_id: sede,
          discipline_id: disciplina,
        });
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return { grupo: r.data as unknown as Grupo, etag: etagDe(r) };
      }

      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupo.id })
        // La disciplina no se cambia: un grupo de kárate no pasa a ser de aikido,
        // y por eso el `PATCH` no la admite.
        .patch(
          { ...comun, venue_id: campos.venue_id },
          {
            headers: etagVivo ? { 'if-match': etagVivo } : {},
          }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { grupo: r.data as unknown as Grupo, etag: etagDe(r) };
    },

    onSuccess: ({ grupo: guardado, etag: nuevo }) => {
      setProblema(null);
      setConflicto(null);
      setTocados(new Set());
      if (nuevo) setEtagVivo(nuevo);
      void cache.invalidateQueries({ queryKey: ['grupos', club] });
      void cache.invalidateQueries({ queryKey: ['grupo', club, guardado.id] });
      alGuardar(guardado.id);
    },

    onError: async (error) => {
      const p = aProblema(error);
      if (!esConflictoDeEdicion(p) || !editando) {
        setProblema(p);
        return;
      }

      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupo.id })
        .get();
      if (r.error || !r.data) {
        setProblema(p);
        return;
      }

      setEtagVivo(etagDe(r));
      setConflicto(
        camposQueCambiaron(
          grupo as unknown as Record<string, unknown>,
          r.data as unknown as Record<string, unknown>,
          [...tocados]
        )
      );
    },
  });

  if (suspendido) {
    return (
      <div className="hueco">
        <h2>La cuenta del club está suspendida</h2>
        <p>
          Puedes consultar los grupos, pero no cambiarlos hasta que se resuelva.
        </p>
        <div className="hueco-accion">
          <button type="button" className="btn-plano" onClick={alCancelar}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate();
      }}
    >
      <h1>{editando ? 'Editar grupo' : 'Nuevo grupo'}</h1>

      {conflicto && (
        <div className="aviso" role="status">
          <strong>Alguien ha cambiado este grupo mientras lo editabas.</strong>{' '}
          {conflicto.length > 0
            ? `Cambió ${conflicto.map((c) => ETIQUETAS[c] ?? c).join(', ')}. Lo tuyo sigue escrito abajo.`
            : 'Los cambios no afectan a lo que estás tocando.'}
        </div>
      )}

      <div className="rejilla">
        <label>
          Nombre
          <input
            required
            maxLength={80}
            placeholder="Infantil L-X 17:30"
            value={campos.name}
            onChange={(e) => cambiar('name', e.target.value)}
          />
        </label>

        <label>
          Sede
          <select
            required
            value={sede}
            onChange={(e) => cambiar('venue_id', e.target.value)}
            disabled={!sedes.data}
          >
            <option value="">Elige una sede</option>
            {(sedes.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        {sedes.data?.length === 0 && (
          <FaltaAntes
            accion="Añadir la sede"
            destino="/club/sedes"
            nuevo
            volver="/grupos?nuevo=1"
          >
            Tu club todavía no tiene ninguna sede, y cada grupo entrena en una.
            Añádela y volverás aquí con ella ya elegida.
          </FaltaAntes>
        )}

        {!editando && (
          <label>
            Disciplina
            <select
              required
              value={disciplina}
              onChange={(e) => cambiar('discipline_id', e.target.value)}
              disabled={!disciplinas.data}
            >
              <option value="">Elige una disciplina</option>
              {suyas.length > 0 ? (
                <>
                  <optgroup label="Las de tu club">
                    {suyas.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Otras">
                    {otras.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </optgroup>
                </>
              ) : (
                otras.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))
              )}
            </select>
          </label>
        )}

        {!editando && delClub.data?.length === 0 && (
          <FaltaAntes
            accion="Elegir sus disciplinas"
            destino="/club/disciplinas"
          >
            Tu club todavía no tiene elegidas sus disciplinas. Elígelas y aquí
            saldrán las primeras, ya puesta si es una sola. Mientras, puedes
            escogerla de la lista.
          </FaltaAntes>
        )}

        <label>
          Nivel
          <input
            maxLength={40}
            placeholder="Opcional: iniciación, avanzado…"
            value={campos.level}
            onChange={(e) => cambiar('level', e.target.value)}
          />
        </label>

        <label>
          Aforo
          <input
            type="number"
            min={1}
            placeholder="Sin límite"
            value={campos.capacity}
            onChange={(e) => cambiar('capacity', e.target.value)}
          />
        </label>
      </div>

      <fieldset className="opciones">
        <legend>Edades</legend>
        <p className="tenue" style={{ margin: '0 0 0.7rem' }}>
          {/* El servidor no lo valida, y la pantalla tampoco debe hacerlo. */}
          Es <strong>informativo</strong>: sirve para orientar, no impide
          matricular a nadie. Un niño grande de 11 puede estar en el grupo de 12
          a 16, y un hermano pequeño con el mayor para que la familia haga un
          solo viaje.
        </p>
        <div className="rejilla" style={{ padding: 0, border: 'none' }}>
          <label>
            Desde
            <input
              type="number"
              min={2}
              max={99}
              placeholder="Sin mínimo"
              value={campos.age_min}
              onChange={(e) => cambiar('age_min', e.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              type="number"
              min={2}
              max={99}
              placeholder="Sin máximo"
              value={campos.age_max}
              onChange={(e) => cambiar('age_max', e.target.value)}
            />
          </label>
        </div>
      </fieldset>

      {problema && (
        <PanelError problema={problema} reintentar={() => guardar.mutate()} />
      )}

      <div className="acciones">
        <button type="submit" disabled={guardar.isPending}>
          {guardar.isPending
            ? 'Guardando…'
            : editando
              ? 'Guardar cambios'
              : 'Crear grupo'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={alCancelar}
          disabled={guardar.isPending}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
